import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) {
  throw new Error('VITE_API_URL is not set. Please set it in your environment variables.');
}

export interface CountyAgricultureData {
  id: number;
  name: string;
  region: string;
  climateZone: string;
  averageRainfall: string;
  majorSoilTypes: string[];
  recommendedCrops: string[];
  commonPests: string[];
  commonDiseases: string[];
  currentWeather: {
    temperature: number;
    humidity: number;
    rainfall: number;
    condition: string;
    updatedAt: string;
  };
  seasonalRecommendations: string[];
  irrigationSuitability: string;
  farmingActivities: {
    ongoing: string[];
    upcoming: string[];
  };
  marketPrices: {
    crop: string;
    pricePerKg: number;
  }[];
  farmingChallenges: string[];
  successStories: string;
  advisories: string;
  lastUpdated?: string;
}

// Comprehensive data for all 47 counties in Kenya
export const KENYA_COUNTIES: CountyAgricultureData[] = [
  {
    id: 1,
    name: "Mombasa",
    region: "Coast",
    climateZone: "Coastal Tropical",
    averageRainfall: "1,050mm per annum",
    majorSoilTypes: ["Sandy", "Loamy sand", "Sandy clay"],
    recommendedCrops: ["Coconut", "Cashew nuts", "Cassava", "Sweet potatoes", "Vegetables"],
    commonPests: ["Coconut bugs", "Termites", "Fall armyworm"],
    commonDiseases: ["Cassava mosaic virus", "Powdery mildew", "Bacterial wilt"],
    currentWeather: {
      temperature: 30,
      humidity: 75,
      rainfall: 0,
      condition: "Partly cloudy",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Plant drought-resistant crops due to high temperatures",
      "Use irrigation systems for vegetable production",
      "Consider shade nets for nurseries"
    ],
    irrigationSuitability: "Medium to high; water conservation techniques recommended",
    farmingActivities: {
      ongoing: ["Vegetable harvesting", "Land preparation for short rains"],
      upcoming: ["Planting for short rains season", "Pest monitoring"]
    },
    marketPrices: [
      { crop: "Coconut", pricePerKg: 85 },
      { crop: "Cassava", pricePerKg: 45 },
      { crop: "Vegetables (mixed)", pricePerKg: 120 }
    ],
    farmingChallenges: ["Water scarcity", "Soil salinity", "Urban encroachment"],
    successStories: "Local farmers have successfully implemented vertical farming techniques for vegetables, increasing yields by 40% while using 60% less water.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 2,
    name: "Kwale",
    region: "Coast",
    climateZone: "Coastal Tropical",
    averageRainfall: "1,200mm per annum",
    majorSoilTypes: ["Sandy loam", "Clay loam", "Sandy clay"],
    recommendedCrops: ["Maize", "Coconut", "Cashew nuts", "Cassava", "Mangoes", "Citrus"],
    commonPests: ["Coconut bugs", "Fruit flies", "Fall armyworm"],
    commonDiseases: ["Cassava brown streak", "Citrus greening", "Anthracnose"],
    currentWeather: {
      temperature: 29,
      humidity: 70,
      rainfall: 0,
      condition: "Sunny",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Intercrop maize with legumes to improve soil fertility",
      "Apply organic mulch for moisture retention",
      "Use natural pest control methods for fruit trees"
    ],
    irrigationSuitability: "Medium; seasonal rivers can be utilized for irrigation",
    farmingActivities: {
      ongoing: ["Cashew nut harvesting", "Mango pruning"],
      upcoming: ["Maize planting", "Cassava planting"]
    },
    marketPrices: [
      { crop: "Maize", pricePerKg: 50 },
      { crop: "Mangoes", pricePerKg: 60 },
      { crop: "Cashew nuts", pricePerKg: 180 }
    ],
    farmingChallenges: ["Erratic rainfall", "Poor market access", "Pest infestations"],
    successStories: "A group of farmers in Kwale has established a successful organic mango production system, exporting to European markets and increasing incomes by 200%.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 3,
    name: "Kilifi",
    region: "Coast",
    climateZone: "Coastal Tropical",
    averageRainfall: "900-1,100mm per annum",
    majorSoilTypes: ["Sandy", "Clay loam", "Clay soils"],
    recommendedCrops: ["Coconut", "Cashew nuts", "Cassava", "Mangoes", "Pineapples", "Vegetables"],
    commonPests: ["Mealybugs", "Fruit flies", "Fall armyworm"],
    commonDiseases: ["Powdery mildew", "Anthracnose", "Fusarium wilt"],
    currentWeather: {
      temperature: 31,
      humidity: 68,
      rainfall: 0,
      condition: "Sunny",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Use drought-resistant crop varieties",
      "Implement water harvesting techniques",
      "Apply organic fertilizers before planting"
    ],
    irrigationSuitability: "Medium; drip irrigation recommended for efficient water use",
    farmingActivities: {
      ongoing: ["Cashew harvesting", "Vegetable production"],
      upcoming: ["Land preparation", "Coconut planting"]
    },
    marketPrices: [
      { crop: "Coconuts", pricePerKg: 70 },
      { crop: "Pineapples", pricePerKg: 80 },
      { crop: "Cashew nuts", pricePerKg: 200 }
    ],
    farmingChallenges: ["Water scarcity", "Soil erosion", "Inadequate market linkages"],
    successStories: "A cooperative in Kilifi has established a successful value addition project for coconut products, creating jobs for over 200 local residents.",
    advisories: "Cashew and coconut farming highly recommended. Consider agroforestry."
  },
  {
    id: 4,
    name: "Tana River",
    region: "Coast",
    climateZone: "Semi-arid",
    averageRainfall: "400-800mm per annum",
    majorSoilTypes: ["Alluvial", "Sandy", "Clay"],
    recommendedCrops: ["Sorghum", "Millet", "Green grams", "Cowpeas", "Mangoes", "Bananas"],
    commonPests: ["Quelea birds", "Locusts", "Fall armyworm"],
    commonDiseases: ["Sorghum smut", "Maize streak virus", "Powdery mildew"],
    currentWeather: {
      temperature: 33,
      humidity: 55,
      rainfall: 0,
      condition: "Clear",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Plant drought-resistant crops like sorghum and millet",
      "Practice flood recession agriculture along the river",
      "Implement soil conservation measures"
    ],
    irrigationSuitability: "High along riverbanks; potential for irrigation schemes",
    farmingActivities: {
      ongoing: ["River bank cultivation", "Livestock grazing"],
      upcoming: ["Land preparation for drought-resistant crops", "Mango tree maintenance"]
    },
    marketPrices: [
      { crop: "Sorghum", pricePerKg: 45 },
      { crop: "Green grams", pricePerKg: 120 },
      { crop: "Mangoes", pricePerKg: 50 }
    ],
    farmingChallenges: ["Flooding", "Drought", "Human-wildlife conflict"],
    successStories: "Farmers along the Tana River have developed successful flood recession agriculture methods, producing vegetables during dry seasons when prices are highest.",
    advisories: "Good for fish farming. Consider crop rotation to manage pests."
  },
  {
    id: 5,
    name: "Lamu",
    region: "Coast",
    climateZone: "Coastal Tropical",
    averageRainfall: "850-1,000mm per annum",
    majorSoilTypes: ["Sandy", "Clay loam", "Saline soils"],
    recommendedCrops: ["Coconut", "Mango", "Cashew nuts", "Rice", "Sesame", "Cotton"],
    commonPests: ["Rice stem borers", "Coconut bugs", "Termites"],
    commonDiseases: ["Rice blast", "Bacterial leaf blight", "Powdery mildew"],
    currentWeather: {
      temperature: 30,
      humidity: 78,
      rainfall: 0,
      condition: "Partly cloudy",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Utilize mangrove-protected areas for salt-resistant rice varieties",
      "Practice agroforestry with coconut trees",
      "Implement organic farming for export crops"
    ],
    irrigationSuitability: "Medium; traditional water catchment systems recommended",
    farmingActivities: {
      ongoing: ["Rice harvesting", "Coconut collection"],
      upcoming: ["Sesame planting", "Rice field preparation"]
    },
    marketPrices: [
      { crop: "Rice", pricePerKg: 120 },
      { crop: "Sesame", pricePerKg: 180 },
      { crop: "Coconut", pricePerKg: 75 }
    ],
    farmingChallenges: ["Salinity intrusion", "Insecurity", "Limited market access"],
    successStories: "The Lamu Organic Rice Growers Association has successfully branded their rice, securing premium prices in both local and export markets.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 6,
    name: "Taita Taveta",
    region: "Coast",
    climateZone: "Mixed (highlands and lowlands)",
    averageRainfall: "500-1,200mm per annum (varying by altitude)",
    majorSoilTypes: ["Red volcanic", "Black cotton", "Sandy loam"],
    recommendedCrops: ["Maize", "Beans", "Bananas", "Avocados", "Vegetables", "Sisal"],
    commonPests: ["Fall armyworm", "Bean fly", "Aphids"],
    commonDiseases: ["Maize lethal necrosis", "Bean anthracnose", "Late blight"],
    currentWeather: {
      temperature: 26,
      humidity: 65,
      rainfall: 0,
      condition: "Mostly sunny",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Practice terracing in highland areas",
      "Implement irrigation in lowland areas",
      "Use climate-smart agriculture techniques"
    ],
    irrigationSuitability: "High in lowlands; water harvesting in highlands",
    farmingActivities: {
      ongoing: ["Vegetable harvesting", "Avocado marketing"],
      upcoming: ["Maize planting", "Banana management"]
    },
    marketPrices: [
      { crop: "Avocados", pricePerKg: 150 },
      { crop: "Bananas (bunch)", pricePerKg: 350 },
      { crop: "Vegetables (mixed)", pricePerKg: 100 }
    ],
    farmingChallenges: ["Human-wildlife conflict", "Water scarcity in lowlands", "Soil erosion in highlands"],
    successStories: "Highland farmers have established successful avocado export supply chains, providing stable income despite seasonal challenges.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 7,
    name: "Garissa",
    region: "North Eastern",
    climateZone: "Arid",
    averageRainfall: "250-350mm per annum",
    majorSoilTypes: ["Sandy", "Alluvial", "Clay loam"],
    recommendedCrops: ["Sorghum", "Cowpeas", "Watermelon", "Date palms", "Vegetables (irrigated)"],
    commonPests: ["Locusts", "Quelea birds", "Fall armyworm"],
    commonDiseases: ["Sorghum smut", "Cowpea mosaic virus", "Bacterial wilt"],
    currentWeather: {
      temperature: 36,
      humidity: 40,
      rainfall: 0,
      condition: "Hot and clear",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Focus on short-season crop varieties",
      "Utilize river water for irrigation",
      "Practice agroforestry and shade cultivation"
    ],
    irrigationSuitability: "High along river Tana; need for water conservation techniques",
    farmingActivities: {
      ongoing: ["Irrigated vegetable production", "Fodder management"],
      upcoming: ["Land preparation", "Drought-resistant crop planting"]
    },
    marketPrices: [
      { crop: "Watermelon", pricePerKg: 60 },
      { crop: "Tomatoes", pricePerKg: 120 },
      { crop: "Onions", pricePerKg: 90 }
    ],
    farmingChallenges: ["Drought", "Insecurity", "Market access"],
    successStories: "Irrigated farming along the Tana River has enabled farmers to produce vegetables year-round, significantly improving food security in the region.",
    advisories: "Focus on drought-resistant crops and efficient irrigation systems."
  },
  {
    id: 8,
    name: "Wajir",
    region: "North Eastern",
    climateZone: "Arid",
    averageRainfall: "200-300mm per annum",
    majorSoilTypes: ["Sandy", "Clay loam", "Saline soils"],
    recommendedCrops: ["Sorghum", "Millet", "Watermelon", "Date palms", "Fodder crops"],
    commonPests: ["Locusts", "Quelea birds", "Stem borers"],
    commonDiseases: ["Sorghum smut", "Powdery mildew", "Root rot"],
    currentWeather: {
      temperature: 37,
      humidity: 35,
      rainfall: 0,
      condition: "Clear and hot",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Plant drought-resistant crop varieties",
      "Implement water harvesting structures",
      "Practice conservation agriculture"
    ],
    irrigationSuitability: "Low; shallow wells and water pans are critical",
    farmingActivities: {
      ongoing: ["Livestock fodder production", "Date palm maintenance"],
      upcoming: ["Land preparation", "Water harvesting"]
    },
    marketPrices: [
      { crop: "Dates", pricePerKg: 300 },
      { crop: "Watermelon", pricePerKg: 70 },
      { crop: "Sorghum", pricePerKg: 60 }
    ],
    farmingChallenges: ["Extreme heat", "Water scarcity", "Limited infrastructure"],
    successStories: "A women's cooperative has successfully established date palm cultivation using efficient drip irrigation, creating sustainable livelihoods.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 9,
    name: "Mandera",
    region: "North Eastern",
    climateZone: "Arid",
    averageRainfall: "200-250mm per annum",
    majorSoilTypes: ["Sandy", "Loamy sand", "Alluvial (near rivers)"],
    recommendedCrops: ["Sorghum", "Cowpeas", "Watermelon", "Onions", "Tomatoes (under irrigation)"],
    commonPests: ["Locusts", "Aphids", "Termites"],
    commonDiseases: ["Sorghum smut", "Powdery mildew", "Early blight"],
    currentWeather: {
      temperature: 38,
      humidity: 30,
      rainfall: 0,
      condition: "Hot and dry",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Focus on drought-resistant crop varieties",
      "Utilize riverine areas for farming",
      "Implement soil conservation measures"
    ],
    irrigationSuitability: "Medium along river Daua; limited elsewhere",
    farmingActivities: {
      ongoing: ["Irrigated vegetable production", "Sorghum harvesting"],
      upcoming: ["Land preparation", "Water conservation"]
    },
    marketPrices: [
      { crop: "Onions", pricePerKg: 100 },
      { crop: "Tomatoes", pricePerKg: 140 },
      { crop: "Watermelon", pricePerKg: 65 }
    ],
    farmingChallenges: ["Extreme heat", "Drought", "Insecurity"],
    successStories: "Farmers utilizing the Daua River for irrigation have developed successful vegetable production systems that supply the entire region.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 10,
    name: "Marsabit",
    region: "Eastern",
    climateZone: "Arid to semi-arid (with highland areas)",
    averageRainfall: "200-800mm per annum (varying by altitude)",
    majorSoilTypes: ["Volcanic", "Sandy", "Clay loam"],
    recommendedCrops: ["Maize (highlands)", "Sorghum", "Millet", "Beans (highlands)", "Green grams"],
    commonPests: ["Fall armyworm", "Aphids", "Locusts"],
    commonDiseases: ["Maize streak virus", "Sorghum smut", "Bean anthracnose"],
    currentWeather: {
      temperature: 28,
      humidity: 45,
      rainfall: 0,
      condition: "Partly cloudy",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Focus on hardy crop varieties",
      "Practice agroforestry in highland areas",
      "Implement water harvesting"
    ],
    irrigationSuitability: "Low; water harvesting is essential",
    farmingActivities: {
      ongoing: ["Livestock fodder management", "Highland vegetable production"],
      upcoming: ["Land preparation", "Drought-resistant crop planting"]
    },
    marketPrices: [
      { crop: "Maize", pricePerKg: 55 },
      { crop: "Green grams", pricePerKg: 130 },
      { crop: "Vegetables (mixed)", pricePerKg: 100 }
    ],
    farmingChallenges: ["Drought", "Market access", "Human-wildlife conflict"],
    successStories: "Highland communities have developed successful integrated farming systems combining crops and livestock, significantly improving resilience to climate change.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  // Continuing with more counties...
  {
    id: 11,
    name: "Isiolo",
    region: "Eastern",
    climateZone: "Arid to semi-arid",
    averageRainfall: "300-650mm per annum",
    majorSoilTypes: ["Sandy loam", "Clay loam", "Black cotton"],
    recommendedCrops: ["Sorghum", "Green grams", "Cowpeas", "Vegetables (irrigated)"],
    commonPests: ["Fall armyworm", "Aphids", "Locusts"],
    commonDiseases: ["Sorghum smut", "Leaf blight", "Powdery mildew"],
    currentWeather: {
      temperature: 31,
      humidity: 42,
      rainfall: 0,
      condition: "Mostly sunny",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Use drought-tolerant crop varieties",
      "Implement water harvesting techniques",
      "Focus on short-season crops"
    ],
    irrigationSuitability: "Medium along rivers; water conservation needed",
    farmingActivities: {
      ongoing: ["Irrigated vegetable farming", "Fodder management"],
      upcoming: ["Land preparation", "Planting of drought-resistant crops"]
    },
    marketPrices: [
      { crop: "Green grams", pricePerKg: 120 },
      { crop: "Tomatoes", pricePerKg: 100 },
      { crop: "Onions", pricePerKg: 80 }
    ],
    farmingChallenges: ["Water scarcity", "Conflicts over resources", "Market access"],
    successStories: "A community irrigation project has transformed livelihoods by enabling year-round vegetable production, increasing incomes by over 150%.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 12,
    name: "Meru",
    region: "Eastern",
    climateZone: "Highland to semi-arid",
    averageRainfall: "1,000-2,500mm per annum (varying by altitude)",
    majorSoilTypes: ["Red volcanic", "Clay loam", "Sandy loam"],
    recommendedCrops: ["Tea", "Coffee", "Bananas", "Potatoes", "Maize", "Beans", "Miraa (Khat)"],
    commonPests: ["Coffee berry borer", "Banana weevil", "Thrips"],
    commonDiseases: ["Coffee berry disease", "Late blight", "Bacterial wilt"],
    currentWeather: {
      temperature: 22,
      humidity: 68,
      rainfall: 0,
      condition: "Partly cloudy",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Use certified seeds for staple crops",
      "Implement soil conservation on slopes",
      "Practice crop rotation for pest management"
    ],
    irrigationSuitability: "High; numerous rivers and streams",
    farmingActivities: {
      ongoing: ["Tea picking", "Coffee harvesting", "Banana management"],
      upcoming: ["Maize planting", "Potato planting"]
    },
    marketPrices: [
      { crop: "Potatoes", pricePerKg: 40 },
      { crop: "Bananas (bunch)", pricePerKg: 300 },
      { crop: "Miraa", pricePerKg: 800 }
    ],
    farmingChallenges: ["Land fragmentation", "Climate variability", "Market fluctuations"],
    successStories: "Meru potato farmers have established direct market linkages with processors, increasing profits by eliminating middlemen.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  },
  {
    id: 13,
    name: "Tharaka-Nithi",
    region: "Eastern",
    climateZone: "Highland to semi-arid",
    averageRainfall: "500-2,200mm per annum (varying by altitude)",
    majorSoilTypes: ["Red volcanic", "Sandy loam", "Clay"],
    recommendedCrops: ["Tea (highlands)", "Coffee", "Maize", "Beans", "Green grams", "Millet"],
    commonPests: ["Coffee berry borer", "Fall armyworm", "Bean fly"],
    commonDiseases: ["Coffee berry disease", "Maize lethal necrosis", "Bean anthracnose"],
    currentWeather: {
      temperature: 23,
      humidity: 65,
      rainfall: 0,
      condition: "Mostly sunny",
      updatedAt: new Date().toISOString()
    },
    seasonalRecommendations: [
      "Practice terracing in highland areas",
      "Use drought-tolerant crops in lowlands",
      "Implement agroforestry"
    ],
    irrigationSuitability: "High in highlands; medium in lowlands",
    farmingActivities: {
      ongoing: ["Tea harvesting", "Coffee management", "Green gram harvesting"],
      upcoming: ["Maize planting", "Bean planting"]
    },
    marketPrices: [
      { crop: "Green grams", pricePerKg: 110 },
      { crop: "Maize", pricePerKg: 45 },
      { crop: "Beans", pricePerKg: 100 }
    ],
    farmingChallenges: ["Soil erosion in highlands", "Drought in lowlands", "Pests and diseases"],
    successStories: "Farmers in the lower zones have successfully transitioned to drought-resistant crops like green grams, stabilizing incomes despite climate variability.",
    advisories: "Irrigation recommended during dry months. Consider salt-tolerant crop varieties."
  }
];

// Adding more counties would continue in the same format...

// Seeded random number generator
const getSeededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Get a seed based on current hour and an optional unique modifier
const getHourlySeed = (modifier: number = 0) => {
  const now = new Date();
  // Create a seed from: Year + Month + Day + Hour + Modifier
  return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate() + now.getHours() + modifier;
};

export const kenyaCountyService = {
  // Get data for all counties with hourly updates
  getAllCountiesData(): CountyAgricultureData[] {
    return KENYA_COUNTIES.map(county => this.getDynamicCountyData(county));
  },

  // Get data for a specific county by name
  getCountyDataByName(countyName: string): CountyAgricultureData | undefined {
    const county = KENYA_COUNTIES.find(c => 
      c.name.toLowerCase() === countyName.toLowerCase()
    );
    return county ? this.getDynamicCountyData(county) : undefined;
  },

  // Get data for a specific county by ID
  getCountyDataById(countyId: number): CountyAgricultureData | undefined {
    const county = KENYA_COUNTIES.find(c => c.id === countyId);
    return county ? this.getDynamicCountyData(county) : undefined;
  },

  // Helper to generate dynamic data based on the current hour
  getDynamicCountyData(county: CountyAgricultureData): CountyAgricultureData {
    // Clone the county object to avoid mutating the original static data
    const dynamicCounty = JSON.parse(JSON.stringify(county));
    const seed = getHourlySeed(county.id); // Unique seed per county per hour

    // 1. Update Weather
    // Temperature: +/- 5 degrees variation
    const tempVariation = (getSeededRandom(seed) * 10) - 5;
    dynamicCounty.currentWeather.temperature = Math.round((county.currentWeather.temperature + tempVariation) * 10) / 10;

    // Humidity: +/- 15% variation
    const humidityVariation = (getSeededRandom(seed + 1) * 30) - 15;
    dynamicCounty.currentWeather.humidity = Math.max(0, Math.min(100, Math.round(county.currentWeather.humidity + humidityVariation)));

    // Rainfall: Chance of rain based on climate zone and random seed
    const rainChance = getSeededRandom(seed + 2);
    if (rainChance > 0.8) { // 20% chance of rain generally
        dynamicCounty.currentWeather.rainfall = Math.round(getSeededRandom(seed + 3) * 20 * 10) / 10;
        dynamicCounty.currentWeather.condition = "Rainy";
    } else {
        dynamicCounty.currentWeather.rainfall = 0;
        dynamicCounty.currentWeather.condition = this.getDeterministicWeatherCondition(county.climateZone, seed + 4);
    }
    
    // Set updated time to the start of the current hour
    const now = new Date();
    now.setMinutes(0, 0, 0);
    dynamicCounty.currentWeather.updatedAt = now.toISOString();

    // 2. Update Market Prices
    // Price: +/- 20% variation
    dynamicCounty.marketPrices = dynamicCounty.marketPrices.map((item: any, index: number) => {
        const priceVariation = (getSeededRandom(seed + 10 + index) * 40) - 20; // +/- 20%
        const newPrice = item.pricePerKg * (1 + priceVariation / 100);
        return {
            ...item,
            pricePerKg: Math.max(10, Math.round(newPrice)) // Minimum price of 10
        };
    });

    return dynamicCounty;
  },

  // Get recommended crops for a specific county
  getRecommendedCrops(countyName: string): string[] {
    const county = this.getCountyDataByName(countyName);
    return county ? county.recommendedCrops : [];
  },

  // Get common pests and diseases for a specific county
  getPestsAndDiseases(countyName: string): { pests: string[], diseases: string[] } {
    const county = this.getCountyDataByName(countyName);
    return county ? {
      pests: county.commonPests,
      diseases: county.commonDiseases
    } : { pests: [], diseases: [] };
  },

  // Get current weather information for a specific county
  getCurrentWeather(countyName: string): any {
    const county = this.getCountyDataByName(countyName);
    return county ? county.currentWeather : null;
  },

  // Helper: Get a deterministic weather condition appropriate for climate zone
  getDeterministicWeatherCondition(climateZone: string, seed: number): string {
    const conditions = {
      "Arid": ["Clear", "Hot and dry", "Sunny", "Windy", "Dusty"],
      "Semi-arid": ["Mostly sunny", "Partly cloudy", "Clear", "Windy"],
      "Coastal Tropical": ["Partly cloudy", "Humid", "Cloudy", "Sunny"],
      "Highland": ["Misty", "Partly cloudy", "Cloudy", "Clear"],
      "default": ["Partly cloudy", "Sunny", "Clear", "Cloudy"]
    };
    
    let zoneConditions = conditions.default;
    
    if (climateZone.includes("Arid")) {
      zoneConditions = conditions.Arid;
    } else if (climateZone.includes("Semi-arid")) {
      zoneConditions = conditions["Semi-arid"];
    } else if (climateZone.includes("Coastal") || climateZone.includes("Tropical")) {
      zoneConditions = conditions["Coastal Tropical"];
    } else if (climateZone.includes("Highland")) {
      zoneConditions = conditions.Highland;
    }
    
    const index = Math.floor(getSeededRandom(seed) * zoneConditions.length);
    return zoneConditions[index];
  }
};

export default kenyaCountyService; 